import { Button, Dialog, Flex, Text, Progress, Callout, Link } from '@radix-ui/themes'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { ExternalLinkIcon, InfoIcon } from 'lucide-react'

import { useAuth } from '@/hooks/useAuth'

const LOCK_MS = 15 * 1000

export const PaymentDueModal = () => {
  const [remainingMs, setRemainingMs] = useState(LOCK_MS)
  const [canDismiss, setCanDismiss] = useState(false)
  const { userData, isAuthenticated } = useAuth()
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('app')

  const settings = userData?.organization?.settings
  const isDue = Boolean(settings?.paymentDue)

  // Show on every sign-in and then every 10 minutes while due
  useEffect(() => {
    if (!isAuthenticated || !isDue) {
      setOpen(false)

      return
    }

    // Show immediately on sign-in/ready
    setOpen(true)

    const id = window.setInterval(() => setOpen(true), 10 * 60 * 1000)

    return () => window.clearInterval(id)
  }, [isAuthenticated, isDue])

  // Lock dismissal for 30s each time the modal opens, with countdown/progress
  useEffect(() => {
    if (!open) {
      return
    }

    setRemainingMs(LOCK_MS)
    setCanDismiss(false)

    const start = Date.now()

    const tick = () => {
      const ms = Math.max(0, LOCK_MS - (Date.now() - start))

      setRemainingMs(ms)

      if (ms === 0) {
        setCanDismiss(true)
        window.clearInterval(id)
      }
    }

    const id = window.setInterval(tick, 100)

    tick()

    return () => window.clearInterval(id)
  }, [open])

  if (!isDue) {
    return null
  }

  const progressValue = (remainingMs / LOCK_MS) * 100

  return (
    <Dialog.Root open={open} onOpenChange={(next) => setOpen(next || !canDismiss)}>
      <Dialog.Content
        maxWidth="520px"
        onEscapeKeyDown={(e) => {
          if (!canDismiss) {
            e.preventDefault()
          }
        }}
        onPointerDownOutside={(e) => {
          if (!canDismiss) {
            e.preventDefault()
          }
        }}
      >
        <Dialog.Title>{t('paymentDueModal.title')}</Dialog.Title>
        <Flex direction="column" gap="4">
          <Text as="p">
            <Trans t={t} values={{ organization: userData?.organization?.name }}>
              paymentDueModal.body
            </Trans>
          </Text>

          {settings?.paymentMessage && <Text>{settings?.paymentMessage}</Text>}

          <Callout.Root>
            <Callout.Icon>
              <InfoIcon />
            </Callout.Icon>
            <Callout.Text>{t('paymentDueModal.reinforcement')}</Callout.Text>
          </Callout.Root>

          {!canDismiss && <Progress value={progressValue} max={100} size="1" />}

          <Flex gap="4" justify="between">
            {settings?.paymentUrl ? (
              <Button asChild variant="outline">
                <Link href={settings.paymentUrl} target="_blank" rel="noreferrer">
                  <ExternalLinkIcon />
                  {t('paymentDueModal.payNow')}
                </Link>
              </Button>
            ) : (
              <Text></Text>
            )}

            <Button onClick={() => setOpen(false)} disabled={!canDismiss}>
              <Text>{t('paymentDueModal.close')}</Text>
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
